/**
 * PlayerCardController — shows the PlayerCard overlay from anywhere.
 *
 * The crew screen mounts a PlayerCard inside its own `.crew-card-slot`. The
 * lobby and in-game surfaces need the same card over the whole viewport, so
 * this controller owns a body-level host and the action wiring once, instead
 * of duplicating `CrewScreen.showPlayerCard` per surface.
 *
 * Friend status comes from a single source (`lookupFriend`, backed by the crew
 * list) so the friends-only chat rule and ADD/REMOVE state are always correct.
 */

import { PlayerCard, type PlayerCardData } from './PlayerCard';
import type { NetworkManager } from '../../../network/NetworkManager';
import type { BlockService } from '../../../services/BlockService';

/** Minimal display info a caller knows about the tapped player. */
export interface PlayerCardInput {
  id: string;
  username: string;
  picture?: string;
  stacks?: number;
  crewTag?: string;
  isOnline?: boolean;
}

/** Friend record fields the card needs — a subset of FriendCellData. */
export interface PlayerCardFriendInfo {
  username?: string;
  picture?: string;
  stacks?: number;
  crewTag?: string;
  isOnline?: boolean;
}

export interface PlayerCardControllerOptions {
  networkManager: NetworkManager;
  /** Returns the friend record if this player is in the local crew, else undefined. */
  lookupFriend: (id: string) => PlayerCardFriendInfo | undefined;
  /** Route to a conversation with a friend (chat is friends-only). */
  onChat: (id: string, username: string) => void;
  /** Source of truth for "have I blocked this player?" (drives the Unblock action). */
  blockService?: BlockService;
  /** True if the local player already sent this player a still-unanswered
   *  friend request (drives the PENDING state instead of ADD). */
  lookupPending?: (id: string) => boolean;
  /** Called when a request is sent from this card so the shared pending list
   *  (owned by CrewScreen) stays in sync. Carries display data for the Crew
   *  tab's pending section. */
  onFriendRequestSent?: (info: { playerId: string; username: string; picture: string; stacks: number }) => void;
}

const HOST_STYLE_ID = 'player-card-host-styles';

export class PlayerCardController {
  private host: HTMLElement;
  private card: PlayerCard | null = null;
  /** Removes the open card's friend:online/offline subscription. */
  private presenceCleanup: (() => void) | null = null;

  constructor(private opts: PlayerCardControllerOptions) {
    this.injectHostStyle();
    this.host = document.createElement('div');
    this.host.className = 'player-card-host';
    document.body.appendChild(this.host);
  }

  /** Open the card for a player. Friend lookup decides isFriend + enriches data. */
  show(input: PlayerCardInput): void {
    const friend = this.opts.lookupFriend(input.id);
    const data: PlayerCardData = {
      id: input.id,
      // Prefer the crew record (freshest) but fall back to caller-provided info.
      username: friend?.username ?? input.username ?? 'Player',
      picture: friend?.picture ?? input.picture ?? '',
      stacks: friend?.stacks ?? input.stacks ?? 0,
      crewTag: friend?.crewTag ?? input.crewTag,
      isOnline: friend?.isOnline ?? input.isOnline ?? false,
      isFriend: !!friend,
      isBlocked: this.opts.blockService?.has(input.id) ?? false,
      isPending: !friend && (this.opts.lookupPending?.(input.id) ?? false),
    };

    this.card?.destroy();
    this.card = new PlayerCard(data);
    this.host.appendChild(this.card.element);
    this.card.show();

    // Live presence while the card is open (friends only — the server doesn't
    // push presence for non-friends, so strangers never show a stale dot).
    this.presenceCleanup?.();
    this.presenceCleanup = null;
    if (data.isFriend) {
      const nm = this.opts.networkManager;
      const onOnline = (d: { playerId: string }) => {
        if (d.playerId === input.id) this.card?.setOnline(true);
      };
      const onOffline = (d: { playerId: string }) => {
        if (d.playerId === input.id) this.card?.setOnline(false);
      };
      nm.on('friend:online', onOnline);
      nm.on('friend:offline', onOffline);
      this.presenceCleanup = () => {
        nm.off('friend:online', onOnline);
        nm.off('friend:offline', onOffline);
      };
    }

    this.card.on('action:chat', ({ playerId }) => {
      this.close();
      this.opts.onChat(playerId, data.username);
    });
    this.card.on('action:add', ({ playerId }) => {
      // PlayerCard flips itself to the PENDING state on click.
      this.opts.networkManager.sendFriendAdd(playerId);
      this.opts.onFriendRequestSent?.({
        playerId,
        username: data.username,
        picture: data.picture,
        stacks: data.stacks ?? 0,
      });
    });
    this.card.on('action:remove', ({ playerId }) => {
      this.opts.networkManager.sendFriendRemove(playerId);
      this.close();
    });
    this.card.on('action:block', ({ playerId }) => {
      this.opts.networkManager.sendFriendBlock(playerId);
      // Optimistic — vanish from lobby/chat now; blocked:list reconciles after.
      this.opts.blockService?.add({ id: playerId, username: data.username, picture: data.picture });
      this.close();
    });
    this.card.on('action:unblock', ({ playerId }) => {
      this.opts.networkManager.sendFriendUnblock(playerId);
      this.opts.blockService?.remove(playerId); // optimistic — server re-sends blocked:list
      this.close();
    });
    this.card.on('action:close', () => this.close());
  }

  close(): void {
    this.presenceCleanup?.();
    this.presenceCleanup = null;
    const card = this.card;
    this.card = null;
    card?.hide();
    // Let the hide transition play, then tear down listeners + DOM.
    setTimeout(() => card?.destroy(), 220);
  }

  private injectHostStyle(): void {
    if (document.getElementById(HOST_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = HOST_STYLE_ID;
    // The card's own styles (.crew-player-card etc.) come from CrewScreen's
    // injected stylesheet and position absolute within this fixed host.
    style.textContent = `
      .player-card-host {
        position: fixed;
        inset: 0;
        z-index: 4000;
        pointer-events: none;
      }
      .player-card-host .crew-player-card { pointer-events: auto; }
    `;
    document.head.appendChild(style);
  }
}
